import { ScrollVideo } from '@pcp/core/interfaces';

/**
 * Pure frame math for scroll-video slides (`SlideType.scrollVideo`, `canvas-scroll-video-slides`).
 *
 * A scroll-video is an image-sequence "scrubber": `template` names numbered frame images
 * (`{imageNo}` placeholder, zero-padded to {@link SCROLL_VIDEO_FRAME_PAD}); frames run from
 * `start` to `end` in increments of `step`. Scrubbing changes a single integer frame index —
 * these helpers are the ONE source of index stepping and frame-URL resolution, shared by the
 * writer (next frame), followers (resolve the synced index), and the preloader (enumerate all
 * frames), so the three can never disagree on padding or grid alignment.
 */

/**
 * Frame filename zero-padding width. Historically an injection token
 * (`SLIDE_SYNC_SCROLL_VIDEO_FILENAME_LENGTH`), but every provider used `4` — and the prefetch
 * path independently hardcoded `4`, so a differently-configured token would have silently
 * diverged prefetched URLs from rendered URLs. A shared constant removes that failure mode.
 */
export const SCROLL_VIDEO_FRAME_PAD = 4;

/**
 * The frame a scroll-video shows before anyone scrubs: the authored `current`, else `start`.
 * Also the frame a layer returns to on a slide show / same-slide reset (the synced
 * `<layer>SlideScrollVideoFrame` field resets to `null`, and display falls back to this seed).
 */
export function scrollVideoSeedFrame(scrollVideo: ScrollVideo): number {
    return scrollVideo.current ?? scrollVideo.start;
}

/**
 * Highest frame index reachable from `start` in whole `step` increments without exceeding `end`.
 * Scrubbing clamps HERE (not at raw `end`): frame images exist on the step grid, so clamping to
 * an off-grid `end` and stepping back would produce indices with no corresponding image.
 */
export function scrollVideoMaxFrame(scrollVideo: ScrollVideo): number {
    const { start, end, step } = scrollVideo;
    if (step <= 0 || end <= start) return start;
    return start + Math.floor((end - start) / step) * step;
}

/**
 * The next frame index after one scrub tick from `currentFrame` — `'right'` advances, `'left'`
 * rewinds — clamped to the step grid within `[start, maxFrame]`. Returns `currentFrame`
 * unchanged when the tick cannot move (already at a boundary): callers treat "unchanged" as the
 * boundary no-op (the legacy builder threw `'Scroll video end reached'` for this).
 */
export function nextScrollVideoFrame(
    scrollVideo: ScrollVideo,
    currentFrame: number,
    direction: 'left' | 'right',
): number {
    const next = direction === 'right' ? currentFrame + scrollVideo.step : currentFrame - scrollVideo.step;
    return Math.min(Math.max(next, scrollVideo.start), scrollVideoMaxFrame(scrollVideo));
}

/**
 * Resolve the image URL for a frame index: substitute the zero-padded index into the template
 * and append the SAS query (frame templates are unsigned — unlike `imageResource`, they never
 * pass through `replaceSignatureUrls`).
 */
export function scrollVideoFrameUrl(scrollVideo: ScrollVideo, frame: number, sas = ''): string {
    return scrollVideo.template.replace('{imageNo}', frame.toString().padStart(SCROLL_VIDEO_FRAME_PAD, '0')) + sas;
}

/** Every frame index of the sequence, on the step grid — the preloader's enumeration. */
export function scrollVideoFrames(scrollVideo: ScrollVideo): number[] {
    const frames: number[] = [];
    const max = scrollVideoMaxFrame(scrollVideo);
    const step = scrollVideo.step > 0 ? scrollVideo.step : 1;
    for (let frame = scrollVideo.start; frame <= max; frame += step) {
        frames.push(frame);
    }
    return frames;
}
